import React, { useMemo, useState } from 'react'
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CFormInput,
  CFormSelect,
  CProgress,
  CRow,
  CSmartTable,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilCloudDownload, cilSearch } from '@coreui/icons'
import { ErrorState, computeDelta, formatNumber } from '../AnalyticsV2/common'
import { useChartTheme } from '../AnalyticsV2/chartTheme'
import { GAME_TYPES, HEALTH, HealthBadge, gameTypeLabel, timeAgo } from './platformHelpers'

const columns = [
  { key: 'game_name', label: 'Game', _style: { minWidth: 220 } },
  { key: 'health', label: 'Health' },
  { key: 'active_users', label: 'Active users' },
  { key: 'online_now', label: 'Online' },
  { key: 'devices', label: 'iPhone / Android', sorter: false, _style: { minWidth: 140 } },
  { key: 'new_app_users', label: 'New app users' },
  { key: 'sessions', label: 'Sessions' },
  { key: 'members', label: 'Members' },
  { key: 'reach_pct', label: 'Reach', _style: { minWidth: 130 } },
  { key: 'new_members', label: 'New members' },
  { key: 'last_active', label: 'Last in app' },
  { key: 'actions', label: '', sorter: false, filter: false },
].map((c) => ({ filter: false, ...c }))

const toCsv = (rows) => {
  const header = [
    'Game ID',
    'Game',
    'Hub',
    'Type',
    'Health',
    'Active users',
    'Previous active users',
    'Sessions',
    'Members',
    'Reach %',
    'New members',
    'iPhone users',
    'Android users',
    'New app users',
    'Online now',
    'Last in app (UTC)',
  ]
  const esc = (v) => {
    const s = v === null || v === undefined ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const lines = rows.map((r) =>
    [
      r.id,
      r.game_name,
      r.hub_name,
      gameTypeLabel(r.game_type),
      r.health,
      r.active_users,
      r.previous_users,
      r.sessions,
      r.members,
      r.reach_pct ?? '',
      r.new_members,
      r.ios_users,
      r.android_users,
      r.new_app_users,
      r.online_now,
      r.last_active ?? '',
    ]
      .map(esc)
      .join(','),
  )
  return [header.join(','), ...lines].join('\n')
}

const GamesPanel = ({ games, live, onOpenGame, onShowUsers, onRetry }) => {
  const theme = useChartTheme()
  const [health, setHealth] = useState('all')
  const [type, setType] = useState('all')
  const [search, setSearch] = useState('')

  const onlineByGame = useMemo(() => {
    const map = {}
    ;(live?.data?.by_game || []).forEach((g) => {
      map[g.game_id] = g.users
    })
    return map
  }, [live?.data])

  const allRows = useMemo(
    () => (games.data?.rows || []).map((g) => ({ ...g, online_now: onlineByGame[g.id] || 0 })),
    [games.data, onlineByGame],
  )
  const counts = games.data?.health || {}

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    return allRows.filter((g) => {
      if (health !== 'all' && g.health !== health) return false
      if (type !== 'all' && String(g.game_type) !== type) return false
      if (!q) return true
      return (
        String(g.id) === q ||
        (g.game_name || '').toLowerCase().includes(q) ||
        (g.hub_name || '').toLowerCase().includes(q)
      )
    })
  }, [allRows, health, type, search])

  const exportCsv = () => {
    const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `platform-games-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  if (games.error) {
    return (
      <CCard>
        <CCardBody>
          <ErrorState message={games.error} onRetry={onRetry} />
        </CCardBody>
      </CCard>
    )
  }

  const chips = [{ key: 'all', label: 'All games', color: 'dark' }, ...HEALTH]

  return (
    <CCard>
      <CCardBody>
        <div className="pa-chips mb-3" role="tablist" aria-label="Filter by health">
          {chips.map((chip) => {
            const count = chip.key === 'all' ? allRows.length : counts[chip.key] || 0
            return (
              <button
                key={chip.key}
                type="button"
                role="tab"
                aria-selected={health === chip.key}
                title={chip.hint}
                className={`pa-chip pa-chip--${chip.color}${health === chip.key ? ' active' : ''}`}
                onClick={() => setHealth(chip.key)}
              >
                {chip.label}
                <span className="pa-chip-count">{games.loading ? '…' : formatNumber(count)}</span>
              </button>
            )
          })}
        </div>

        <CRow className="g-2 mb-3 align-items-center">
          <CCol xs={12} md={5} lg={4}>
            <div className="position-relative">
              <CIcon icon={cilSearch} className="pa-search-icon text-medium-emphasis" />
              <CFormInput
                size="sm"
                className="ps-5"
                placeholder="Search game, hub or game ID"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search games"
              />
            </div>
          </CCol>
          <CCol xs={8} md={4} lg={3}>
            <CFormSelect
              size="sm"
              value={type}
              onChange={(e) => setType(e.target.value)}
              aria-label="Game type"
            >
              <option value="all">All game types</option>
              {Object.entries(GAME_TYPES).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </CFormSelect>
          </CCol>
          <CCol xs={4} md className="text-end">
            <CButton
              color="secondary"
              variant="outline"
              size="sm"
              onClick={exportCsv}
              disabled={!rows.length}
            >
              <CIcon icon={cilCloudDownload} className="me-1" />
              Export
            </CButton>
          </CCol>
        </CRow>

        <div className="ge-datatable ge-datatable--no-select pa-games-table">
          <CSmartTable
            items={rows}
            columns={columns}
            loading={games.loading}
            columnSorter
            sorterValue={{ column: 'active_users', state: 'desc' }}
            pagination
            itemsPerPage={25}
            itemsPerPageSelect
            noItemsLabel="No games match these filters"
            clickableRows
            onRowClick={(item) => onOpenGame({ id: item.id, game_name: item.game_name })}
            tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
            scopedColumns={{
              game_name: (item) => (
                <td>
                  <div className="fw-semibold text-truncate pa-game-name" title={item.game_name}>
                    {item.game_name}
                  </div>
                  <div className="small text-medium-emphasis text-truncate">
                    #{item.id} · {item.hub_name || 'No hub'} · {gameTypeLabel(item.game_type)}
                  </div>
                </td>
              ),
              health: (item) => (
                <td>
                  <HealthBadge health={item.health} />
                </td>
              ),
              active_users: (item) => {
                const d = computeDelta(item.active_users, item.previous_users)
                return (
                  <td>
                    <div className="fw-semibold">{formatNumber(item.active_users)}</div>
                    <div className={`small text-${d.color}`}>{d.label}</div>
                  </td>
                )
              },
              online_now: (item) => (
                <td className="text-nowrap">
                  {item.online_now > 0 ? (
                    <span className="d-inline-flex align-items-center gap-1 fw-semibold text-success">
                      <span className="pa-online-dot" aria-hidden="true" />
                      {formatNumber(item.online_now)}
                    </span>
                  ) : (
                    <span className="text-medium-emphasis">0</span>
                  )}
                </td>
              ),
              devices: (item) => {
                const total = item.ios_users + item.android_users
                return (
                  <td>
                    <div className="small text-nowrap">
                      <strong>{formatNumber(item.ios_users)}</strong> iPhone ·{' '}
                      <strong>{formatNumber(item.android_users)}</strong> Android
                    </div>
                    {total > 0 && (
                      <div className="pa-split pa-split--thin mt-1" aria-hidden="true">
                        <span
                          style={{
                            width: `${(item.ios_users / total) * 100}%`,
                            background: theme.series[0],
                          }}
                        />
                        <span
                          style={{
                            width: `${(item.android_users / total) * 100}%`,
                            background: theme.series[1],
                          }}
                        />
                      </div>
                    )}
                  </td>
                )
              },
              new_app_users: (item) => (
                <td>
                  {item.new_app_users > 0 ? (
                    <span className="text-success fw-semibold">
                      +{formatNumber(item.new_app_users)}
                    </span>
                  ) : (
                    <span className="text-medium-emphasis">0</span>
                  )}
                </td>
              ),
              sessions: (item) => <td>{formatNumber(item.sessions)}</td>,
              members: (item) => <td>{formatNumber(item.members)}</td>,
              reach_pct: (item) => (
                <td>
                  {item.reach_pct === null ? (
                    <span className="text-medium-emphasis">—</span>
                  ) : (
                    <div title="Share of members who opened the app this period">
                      <div className="small fw-semibold">{item.reach_pct}%</div>
                      <CProgress
                        thin
                        value={item.reach_pct}
                        color={
                          item.reach_pct >= 50
                            ? 'success'
                            : item.reach_pct >= 20
                            ? 'info'
                            : 'warning'
                        }
                      />
                    </div>
                  )}
                </td>
              ),
              new_members: (item) => (
                <td>
                  {item.new_members > 0 ? (
                    <span className="text-success fw-semibold">
                      +{formatNumber(item.new_members)}
                    </span>
                  ) : (
                    <span className="text-medium-emphasis">0</span>
                  )}
                </td>
              ),
              last_active: (item) => (
                <td className="text-nowrap">
                  {timeAgo(item.last_active) || (
                    <span className="text-medium-emphasis">Not this period</span>
                  )}
                </td>
              ),
              actions: (item) => (
                <td className="text-end text-nowrap">
                  <CButton
                    color="primary"
                    variant="outline"
                    size="sm"
                    className="me-1"
                    onClick={(e) => {
                      e.stopPropagation()
                      onOpenGame({ id: item.id, game_name: item.game_name })
                    }}
                  >
                    Insights
                  </CButton>
                  <CButton
                    color="secondary"
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      onShowUsers(item.id)
                    }}
                  >
                    Users
                  </CButton>
                </td>
              ),
            }}
          />
        </div>
      </CCardBody>
    </CCard>
  )
}

export default GamesPanel
